/**
 * <quick-add-card> — 纯业务加购组件（Slot 方案）
 *
 * 职责：
 * - 管理加购状态机（idle → selecting → loading → success/error → idle）
 * - 多规格商品：渲染 modal 骨架（DOM 结构），样式由 LLM 在 section CSS 中定义
 * - 单规格商品：直接加购
 * - 调用 Cart API（/cart/add.js）
 * - 通过 data-state 属性暴露当前状态
 *
 * 用法：
 * <quick-add-card
 *   product-id="123"
 *   variant-id="456"
 *   variants='[...]'
 *   options='[...]'
 *   variant-mode="modal"
 *   product-title="Product Name"
 *   product-image="https://..."
 * >
 *   <button slot="add-btn">Add to Cart</button>
 * </quick-add-card>
 *
 * variant-mode：modal（居中弹框）
 *
 * 骨架 DOM 结构（渲染到 document.body）：
 *
 * .qac-wrapper[data-variant-mode]
 *   .qac-overlay
 *   .qac-modal
 *     .qac-header
 *       .qac-product-title
 *       button.qac-close
 *     .qac-body
 *       .qac-product-media > img.qac-product-img
 *       .qac-product-info
 *         <variant-selector>          ← 复用 variant-selector 组件
 *           .vs-product-price
 *           .vs-option-group[data-option-name]
 *             .vs-option-label
 *             .vs-option-values
 *               button.vs-option-btn[data-value-id][data-swatch-type]
 *     .qac-footer > button.qac-submit
 *
 * 状态：data-state = idle | selecting | loading | success | error
 *
 * 事件：
 * - qac:state-change   { state, prevState }
 * - qac:add-success    { cartData }
 * - qac:add-error      { error }
 * - qac:variant-change { variantId, variant }
 */

if (!customElements.get("ai-quick-add-cart")) {
  customElements.define(
    "ai-quick-add-cart",
    class QuickAddCard extends HTMLElement {
      // ─── 状态 ───
      _state = "idle";
      _selectedVariantId = null;
      _variants = [];
      _options = [];
      _variantSelector = null; // modal wrapper
      _variantMode = "modal";

      // ══════════════════════════════════════════
      //  生命周期
      // ══════════════════════════════════════════

      connectedCallback() {
        this.style = 'display:block;position:relative'
        // 优先从属性读，fallback 到父容器 <script> 标签
        this._variants = this._parseJSON(this.getAttribute("variants")) || this._getProductData("variants") || [];
        this._options = this._parseJSON(this.getAttribute("options")) || this._getProductData("options") || [];
        this._selectedVariantId = this.getAttribute("variant-id") || null;
        this._variantMode = this.getAttribute("variant-mode") || "modal";

        this._setState("idle");
        this._bindAddButton();

        // 监听兄弟 <ai-variant-selector> 的 variant:change 事件
        this._variantChangeHandler = e => {
          if (e.target.tagName === 'AI-VARIANT-SELECTOR') {
            this._selectedVariantId = String(e.detail.variantId);
            this.setAttribute("variant-id", this._selectedVariantId);
          }
        };
        this.parentElement?.addEventListener("variant:change", this._variantChangeHandler);
      }

      disconnectedCallback() {
        this._closeSelector();
        if (this._variantChangeHandler) {
          this.parentElement?.removeEventListener("variant:change", this._variantChangeHandler);
        }
      }

      // ══════════════════════════════════════════
      //  点击入口
      // ══════════════════════════════════════════

      _bindAddButton() {
        const btn = this.querySelector('[slot="add-btn"]');
        if (!btn) return;

        // 自动包裹文本节点为 <span class="qac-btn-text">
        // 这样状态切换只改 span，不破坏按钮内其他 DOM（图标等）
        this._wrapBtnText(btn);

        btn.addEventListener("click", e => {
          e.preventDefault();
          e.stopPropagation();
          this._handleAddClick();
        });
      }

      /**
       * 找到按钮内的第一个非空文本节点，包裹为 <span class="qac-btn-text">
       * 如果已有 .qac-btn-text 则跳过
       */
      _wrapBtnText(btn) {
        if (btn.querySelector(".qac-btn-text")) return;

        const textNode = Array.from(btn.childNodes).find(
          n => n.nodeType === Node.TEXT_NODE && n.textContent.trim(),
        );
        if (textNode) {
          const span = document.createElement("span");
          span.className = "qac-btn-text";
          span.textContent = textNode.textContent.trim();
          textNode.replaceWith(span);
        } else {
          // 没有裸文本节点 → 在末尾追加
          const span = document.createElement("span");
          span.className = "qac-btn-text";
          span.textContent = btn.textContent.trim() || "Add to Cart";
          // 清空现有文本但保留元素子节点
          btn.appendChild(span);
        }
      }

      _handleAddClick() {
        if (this._state === "loading" || this._state === "selecting") return;

        const isSingleVariant = this._variants.length <= 1;
        // PDP 场景：寻找包含当前卡片的共同父容器（通常在最近的 section 或独立块中）里的 variant-selector
        // 为了防止 HTML 结构嵌套导致 parentElement 找不到，向上查找到最接近的包裹容器
        // 这里可以直接查 document 内所有带有 selected-variant-id 的选择器，但最稳妥是找最近的 product 容器
        const productContainer = this.closest('ai-product-detail, ai-product-card, [id^="genstore-section"]') || this.parentElement;
        const hasInlineSelector = !!productContainer?.querySelector("ai-variant-selector");

        if (isSingleVariant || hasInlineSelector) {
          const vid = this._selectedVariantId || this._variants[0]?.id;
          if (vid) this._addToCart(vid);
        } else {
          this._showVariantSelector();
        }
      }

      // ══════════════════════════════════════════
      //  选择器：打开 / 关闭
      // ══════════════════════════════════════════

      _showVariantSelector() {
        if (this._variantSelector) {
          this._closeSelector();
          return;
        }

        this._setState("selecting");

        const mode = this._variantMode;

        // 组装骨架
        const wrapper = this._createWrapper(mode);
        const container = this._createContainer(mode);
        container.appendChild(this._createHeader());
        container.appendChild(this._createBody());
        container.appendChild(this._createFooter());
        wrapper.appendChild(container);

        // 挂到 body
        this._variantSelector = wrapper;
        document.body.appendChild(wrapper);

        // 锁定滚动
        this._prevBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        // 下一帧添加 data-open（CSS transition 入场）
        requestAnimationFrame(() => wrapper.setAttribute("data-open", ""));

        // ESC 关闭
        this._escHandler = e => {
          if (e.key === "Escape") this._closeSelector();
        };
        document.addEventListener("keydown", this._escHandler);
      }

      _closeSelector() {
        if (!this._variantSelector) return;

        this._variantSelector.removeAttribute("data-open");
        document.body.style.overflow = this._prevBodyOverflow || "";

        if (this._escHandler) {
          document.removeEventListener("keydown", this._escHandler);
          this._escHandler = null;
        }

        const el = this._variantSelector;
        this._variantSelector = null;
        setTimeout(() => el.remove(), 300);

        this._setState("idle");
      }

      // ══════════════════════════════════════════
      //  UI 构建（按区域拆分）
      // ══════════════════════════════════════════

      _createWrapper(mode) {
        const wrapper = document.createElement("div");
        wrapper.className = "qac-wrapper";
        wrapper.setAttribute("data-variant-mode", mode);
        this._copyColorTokens(wrapper);

        const overlay = document.createElement("div");
        overlay.className = "qac-overlay";
        overlay.addEventListener("click", () => this._closeSelector());
        wrapper.appendChild(overlay);

        return wrapper;
      }

      _createContainer(mode) {
        const container = document.createElement("div");
        container.className = `qac-${mode} ${this._resolveColorSchemeClass()}`;
        container.setAttribute(
          "role",
          mode === "modal" ? "dialog" : "complementary",
        );
        container.setAttribute("aria-modal", "true");
        container.setAttribute("aria-label", "Select product options");

        return container;
      }

      _copyColorTokens(target) {
        // 直接读组件自身「继承后」的 token 值。<ai-quick-add-cart> 位于 section 子树内，
        // 无论 token 注入在 .section-mark（ai-container）、.shopify-container（PDP）、
        // #genstore-section-* 还是 :root，都会通过 CSS 继承落到这里。
        // 旧实现用 closest 去猜某个祖先 class，会漏掉 ai-container 注入的 .section-mark，
        // 进而 closest 命中其父级 #genstore-section-*（token 不向上继承）→ 读到空 → 不拷贝。
        const cs = getComputedStyle(this);
        const colorTokens = [
          "--background",
          "--foreground",
          "--brand",
          "--brand-foreground",
          "--secondary",
          "--secondary-foreground",
          "--striking",
          "--striking-foreground",
          "--accent",
          "--accent-foreground",
          "--cta",
          "--cta-foreground",
        ];
        for (const v of colorTokens) {
          const val = cs.getPropertyValue(v);
          if (val) target.style.setProperty(v, val.trim());
        }
      }

      _resolveColorSchemeClass() {
        const el = this.closest("[data-color-scheme]");
        if (el) {
          const scheme = el.getAttribute("data-color-scheme");
          const idx = scheme.replace("scheme-", "");
          return `color-scheme-${idx}`;
        }
        const ancestor = this.closest("[class*='color-scheme-']");
        if (ancestor) {
          const match = ancestor.className.match(/color-scheme-(\d+)/);
          if (match) return `color-scheme-${match[1]}`;
        }
        return "color-scheme-1";
      }

      _createHeader() {
        const header = document.createElement("div");
        header.className = "qac-header";

        const productTitle = this.getAttribute("product-title");
        if (productTitle) {
          const titleEl = document.createElement("span");
          titleEl.className = "qac-product-title";
          titleEl.textContent = productTitle;
          header.appendChild(titleEl);
        }

        const closeBtn = document.createElement("button");
        closeBtn.className = "qac-close";
        closeBtn.type = "button";
        closeBtn.setAttribute("aria-label", "Close");
        closeBtn.innerHTML = "&times;";
        closeBtn.addEventListener("click", () => this._closeSelector());
        header.appendChild(closeBtn);

        return header;
      }

      _createBody() {
        const body = document.createElement("div");
        body.className = "qac-body";
        body.style.padding = "24px"
        body.appendChild(this._createMedia());
        body.appendChild(this._createProductInfo());

        return body;
      }

      _createMedia() {
        const mediaEl = document.createElement("div");
        mediaEl.className = "qac-product-media";

        const img = document.createElement("img");
        img.className = "qac-product-img";
        const defaultImage = this.getAttribute("product-image");
        if (defaultImage) {
          img.src = defaultImage;
          img.alt = this.getAttribute("product-title") || "";
        }
        mediaEl.appendChild(img);

        return mediaEl;
      }

      _createProductInfo() {
        const infoEl = document.createElement("div");
        infoEl.className = "qac-product-info";

        // 内嵌 <ai-variant-selector> 复用选项构建和匹配逻辑
        const vs = document.createElement("ai-variant-selector");
        vs.setAttribute("variants", JSON.stringify(this._variants));
        vs.setAttribute("options", JSON.stringify(this._options));
        if (this._selectedVariantId) {
          vs.setAttribute("selected-variant-id", String(this._selectedVariantId));
        }
        const priceTemplate = this._getPriceTemplate();
        if (priceTemplate) {
          vs.setAttribute("price-template", priceTemplate);
        }

        // 监听 variant:change 事件，更新图片和提交按钮
        vs.addEventListener("variant:change", e => {
          const { variantId, variant } = e.detail;
          this._selectedVariantId = variantId;

          // 更新图片
          const variantImg = this._getVariantImage(variant);
          if (variantImg) {
            const imgEl = this._variantSelector?.querySelector(".qac-product-img");
            if (imgEl) imgEl.src = variantImg.src
          }

          // 更新提交按钮
          const submitBtn = this._variantSelector?.querySelector(".qac-submit");
          if (submitBtn) {
            submitBtn.disabled = !variant.available;
            if (!variant.available) {
              submitBtn.setAttribute("data-sold-out", "");
            } else {
              submitBtn.removeAttribute("data-sold-out");
            }
          }

          this.dispatchEvent(
            new CustomEvent("qac:variant-change", {
              bubbles: true,
              detail: { variantId, variant },
            }),
          );
        });

        infoEl.appendChild(vs);
        return infoEl;
      }

      _createFooter() {
        const footer = document.createElement("div");
        footer.className = "qac-footer";

        const submitBtn = document.createElement("button");
        submitBtn.className = "qac-submit";
        submitBtn.type = "button";
        submitBtn.disabled = true;

        // 用 span 包裹文字，和 slot 按钮保持一致
        const btnText = document.createElement("span");
        btnText.className = "qac-btn-text";
        btnText.textContent =
          this._getSlotBtnText() || "Add to Cart";
        submitBtn.appendChild(btnText);

        submitBtn.addEventListener("click", () => {
          if (this._selectedVariantId && !submitBtn.disabled) {
            this._addToCart(this._selectedVariantId);
          }
        });
        footer.appendChild(submitBtn);

        return footer;
      }

      /** 获取 slot 按钮的原始文案 */
      _getSlotBtnText() {
        const slotBtn = this.querySelector('[slot="add-btn"]');
        if (!slotBtn) return null;
        const span = slotBtn.querySelector(".qac-btn-text");
        return span?.textContent?.trim() || slotBtn.textContent?.trim() || null;
      }

      // ══════════════════════════════════════════
      //  数据解析工具
      // ══════════════════════════════════════════

      /** 从兄弟 或 父容器内的 <ai-quantity-input> 读取数量，无则返回 1 */
      _getQuantity() {
        const productContainer = this.closest('[id^="genstore-section"]') || this.parentElement;
        const qtyInput = productContainer?.querySelector("ai-quantity-input, quantity-input");
        if (qtyInput) {
          return parseInt(qtyInput.getAttribute("value")) || parseInt(qtyInput.value) || 1;
        }
        return 1;
      }

      _getVariantImage(variant) {
        if (!variant) return null;

        const fi = variant.featured_image;
        if (fi) {
          const src = typeof fi === "string" ? fi : fi.src || fi.url || null;
          if (src) return src;
        }

        const img = variant.image;
        if (img) {
          const src =
            typeof img === "string" ? img : img.src || img.url || null;
          return src;
        }

        return variant.featured_image_url || variant.image_url || null;
      }

      _getPriceTemplate() {
        const explicit = this.getAttribute("price-template");
        if (explicit?.trim()) return explicit.trim();

        const productContainer = this.closest('ai-product-card, ai-product-detail, [id^="genstore-section"]') || this.parentElement;
        return productContainer?.querySelector("[data-product-price]")?.textContent?.trim() || "";
      }

      // ══════════════════════════════════════════
      //  Cart API
      // ══════════════════════════════════════════

      async _addToCart(variantId) {
        this._setState("loading");
        // 单规格 → slot 按钮；多规格 → modal 提交按钮
        const submitBtn =
          this._variantSelector?.querySelector(".qac-submit") ||
          this.querySelector('[slot="add-btn"]');
        const textSpan = submitBtn?.querySelector(".qac-btn-text");
        const originalText = textSpan?.textContent || "Add to Cart";

        // loading 状态：更新按钮
        if (submitBtn) {
          submitBtn.disabled = true;
          if (textSpan) textSpan.textContent = "Adding...";
          submitBtn.setAttribute("data-loading", "");
        }

        try {
          const root = window.genstore?.routes?.root || "";
          const response = await fetch(`${root}/cart/add.js`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              items: [{ id: parseInt(variantId), quantity: this._getQuantity() }],
              sections: "ai-cart-icon-bubble,cart-icon-bubble",
            }),
          });

          if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(
              err.description || err.message || `HTTP ${response.status}`,
            );
          }

          const cartData = await response.json();

          this._setState("success");

          // success 状态：更新按钮
          if (submitBtn) {
            submitBtn.removeAttribute("data-loading");
            submitBtn.setAttribute("data-success", "");
            if (textSpan) textSpan.textContent = "Added \u2713";
          }

          this.dispatchEvent(
            new CustomEvent("qac:add-success", {
              bubbles: true,
              detail: { cartData },
            }),
          );

          const cartUpdateDetail = {
            source: "quick-add-card",
            productVariantId: variantId,
            cartData,
          };
          if (window.AIThemeEvents?.publishCartUpdate) {
            window.AIThemeEvents.publishCartUpdate(cartUpdateDetail);
          }
          if (typeof publish === "function" && window.PUB_SUB_EVENTS?.cartUpdate) {
            publish(PUB_SUB_EVENTS.cartUpdate, cartUpdateDetail);
          }

          this._renderCartBubbleSections(cartData);

          // 1.5s 后自动关闭/恢复
          setTimeout(() => {
            if (this._variantSelector) {
              this._closeSelector();
            } else {
              // 单规格：恢复按钮状态
              if (submitBtn) {
                submitBtn.removeAttribute("data-success");
                if (textSpan) textSpan.textContent = originalText;
                submitBtn.disabled = false;
              }
              this._setState("idle");
            }
          }, 1500);
        } catch (error) {
          console.error("[quick-add-card] Add to cart failed:", error);
          this._setState("error");

          // error 状态：更新按钮，不关闭
          if (submitBtn) {
            submitBtn.removeAttribute("data-loading");
            submitBtn.setAttribute("data-error", "");
            if (textSpan) textSpan.textContent = "Failed";
            // 2s 后恢复按钮，允许重试
            setTimeout(() => {
              submitBtn.removeAttribute("data-error");
              if (textSpan) textSpan.textContent = originalText;
              submitBtn.disabled = false;
              this._setState(this._variantSelector ? "selecting" : "idle");
            }, 2000);
          }

          this.dispatchEvent(
            new CustomEvent("qac:add-error", {
              bubbles: true,
              detail: { error: error.message },
            }),
          );
        }
      }

      _renderCartBubbleSections(cartData) {
        const sections = cartData?.sections;
        if (!sections) return;
        const targets = [
          { section: "ai-cart-icon-bubble", selector: ".ai-cart-bubble-anchor" },
          { section: "cart-icon-bubble", selector: ".cart-count-bubble" },
        ];
        targets.forEach(({ section, selector }) => {
          const html = sections[section];
          if (!html) return;
          const target = document.querySelector(selector);
          if (!target) return;
          const source = new DOMParser().parseFromString(html, "text/html").querySelector(selector);
          if (source) target.innerHTML = source.innerHTML;
        });
      }

      // ══════════════════════════════════════════
      //  状态管理 & 工具
      // ══════════════════════════════════════════

      _setState(state) {
        const prev = this._state;
        if (prev === state) return;
        this._state = state;
        this.setAttribute("data-state", state);

        this.dispatchEvent(
          new CustomEvent("qac:state-change", {
            bubbles: true,
            detail: { state, prevState: prev },
          }),
        );
      }

      _parseJSON(str) {
        if (!str) return null;
        try {
          return JSON.parse(str);
        } catch {
          return null;
        }
      }

      /**
       * 从父容器（ai-product-detail / ai-product-card）的 script 标签读取数据
       * @param {'variants'|'options'} key
       * @returns {Array|null}
       */
      _getProductData(key) {
        const container = this.closest("ai-product-detail, ai-product-card");
        if (!container) return null;
        const script = container.querySelector(`script[data-product-${key}]`);
        if (!script) return null;
        try {
          return JSON.parse(script.textContent);
        } catch {
          return null;
        }
      }
    },
  );
}
